import { useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { Segment, SidebarPushable, SidebarPusher } from "semantic-ui-react";

import { ErrorFallback, MenuItem, MenuSection, MenuView, NavBar, NewsStripe, OverlayLoader, useIsAllLocationsSelected } from "components";
import { NavActionsProvider } from "hooks";
import "layouts/PrivateRouteLayout/PrivateRouteLayout.scss";
import { usePrivateRouteSetup } from "layouts/PrivateRouteLayout/usePrivateRouteSetup";
import { ACL_ACTIVITIES, ACL_RESOURCES } from "models";
import { LoginModal } from "modules/Login/components";
import { useUnreadArticlesQuery } from "modules/NewsDashboard/hooks";
import { PATHS } from "router/paths";

type PrivateRouteLayoutProps = {
  children: React.ReactNode;
};

export const PrivateRouteLayout = ({ children }: PrivateRouteLayoutProps) => {
  const { t } = useTranslation();

  const [showSidebarMenu, setShowSidebarMenu] = useState(false);

  const { isReady, selectedLocation } = usePrivateRouteSetup();
  const { isAllLocationsSelected } = useIsAllLocationsSelected();
  const { unreadArticles = [] } = useUnreadArticlesQuery(selectedLocation?.id);

  const isAppointments = useLocation().pathname === PATHS.APPOINTMENTS;

  if (!isReady) return <OverlayLoader />;

  const hasNewsStripe = unreadArticles.length > 0;

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <div className="main-container">
        <LoginModal />
        <SidebarPushable as={Segment} className="main-container-pushable">
          <MenuView isSideBarOpen={showSidebarMenu} hideSidebar={() => setShowSidebarMenu(false)}>
            <MenuSection title={t("v8_dashboards", "Dashboards")}>
              <MenuItem title={t("v8_appointment_list", "Appointment List")} icon="list" link={PATHS.APPOINTMENTS} resource="appointments-page" activity="browse" />
              <MenuItem
                title={t("v8_workshop_day_overview", "Workshop Day Overview")}
                icon="square kanban regular"
                link={PATHS.DAYPLANNER}
                resource="dayplanner-page"
                activity="browse"
                condition={selectedLocation?.is_dayplanner_enabled}
              />
              <MenuItem title={t("v8_leads", "Leads")} icon="clock" link={PATHS.LEADS_DASHBOARD} resource="leads-dashboard-page" activity="browse" />
              <MenuItem
                title={t("v8_warranty", "Warranty")}
                icon="shield alternate"
                link={PATHS.WARRANTY_DASHBOARD}
                resource="warranty-dashboard-page"
                activity="browse"
              />
              <MenuItem title={t("v8_manufacturer", "Manufacturer")} icon="factory" link={PATHS.MANUFACTURER_DASHBOARD} resource="manufacturer-page" activity="browse" />
              <MenuItem title={t("v8_backorders", "Backorders")} textIcon="BO" link={PATHS.BACKORDERS_DASHBOARD} resource="backorders-dashboard-page" activity="browse" />
              <MenuItem
                title={t("v8_three_department", "Three Department Checks")}
                icon="circle check"
                link={PATHS.THREE_DEPARTMENT_DASHBOARD}
                resource="appointments-three-departments-page"
                activity="browse"
                condition={selectedLocation?.is_three_departments_enabled}
              />
              <MenuItem title={t("v8_users", "Users")} icon="users" link={PATHS.USERS} resource="users-page" activity="browse" />

              <MenuItem
                title={t("v8_privacy_and_security", "Privacy and Security")}
                icon="lock"
                link={PATHS.PRIVACY_AND_SECURITY_DASHBOARD}
                resource="privacy-security-page"
                activity="browse"
              />
              <MenuItem
                title={t("v8_customers_overview", "Customers Overview")}
                icon="address card"
                link={PATHS.CUSTOMERS_DASHBOARD}
                resource="customers-page"
                activity="browse"
              />
              {selectedLocation?.is_tyre_team_enabled && !isAllLocationsSelected && (
                <MenuItem
                  title={t("v8_tyre_discounts", "Tyre Discounts")}
                  icon="tire"
                  link={PATHS.TYRE_DISCOUNTS_DASHBOARD}
                  resource="tyre-discount-page"
                  activity="browse"
                />
              )}
              <MenuItem title={t("v8_webhooks", "Webhooks")} icon="linkify" link={PATHS.WEBHOOKS_DASHBOARD} resource="webhooks-page" activity="browse" />
            </MenuSection>
            <MenuSection title={t("v8_holding", "Holding")}>
              <MenuItem title={t("v8_holdings", "Holdings")} icon="building" link={PATHS.HOLDINGS_DASHBOARD} resource="holding" activity="browse" />
            </MenuSection>
            <MenuSection title={t("v8_reports", "Reports")}>
              <MenuItem title={t("v8_dealer_reports", "Dealer Reports")} icon="chart column" link={PATHS.DEALER_REPORTS} resource="reports-page" activity="browse" />
              <MenuItem
                title={t("v8_location_reports", "Location Reports")}
                icon="chart column"
                link={PATHS.LOCATION_REPORTS}
                resource="locations-reports-page"
                activity="browse"
              />
            </MenuSection>
            <MenuSection title={t("v8_dms_v3", "DMSv3")}>
              <MenuItem
                title={t("v8_dms_settings", "DMS Settings")}
                icon="settings"
                link={PATHS.DMSV3_SETTINGS}
                resource={ACL_RESOURCES.DMSv3SettingPage}
                activity={ACL_ACTIVITIES.Browse}
              />
              <MenuItem
                title={t("v8_custom_queries", "Custom Queries")}
                icon="database"
                link={PATHS.DMSV3_CUSTOM_QUERIES}
                resource="dmsv3-customquery-page"
                activity="browse"
              />
              <MenuItem title={t("v8_monitoring", "Monitoring")} icon="list" link={PATHS.DMSv3_MONITORING_DASHBOARD} resource="dmsv3-monitoring-page" activity="browse" />
              <MenuItem
                title={t("v8_dms_list", "DMS List")}
                icon="table cells"
                link={PATHS.DMS_CAPABILITIES_DASHBOARD}
                resource="dms-capabilities-page"
                activity="browse"
              />
            </MenuSection>
            <MenuSection title={t("v8_resources", "Resources")}>
              <MenuItem title={t("v8_news", "News")} icon="newspaper" link={PATHS.NEWS_DASHBOARD} resource="news-page" activity="browse" />
              <MenuItem
                title={t("v8_dealer_documentation", "Dealer Documentation")}
                icon="file"
                link={PATHS.DEALER_DOCUMENTATION}
                resource="dealer-documentation-page"
                activity="browse"
              />
              <MenuItem title={t("v8_claire_api", "Claire API")} icon="server" link={PATHS.ACCESS_KEYS_DASHBOARD} resource="access-keys-page" activity="browse" />
            </MenuSection>
            <MenuSection title={t("v8_communications", "Communications")}>
              <MenuItem
                title={t("v8_settings", "Settings")}
                icon="closed captioning outline"
                link={PATHS.CUSTOMER_COM}
                activity="browse"
                resource="customer-comm-settings-page"
              />
              <MenuItem
                title={t("v8_communication_templates", "Templates")}
                icon="film outline"
                link={PATHS.COMMUNICATION_TEMPLATES}
                activity="browse"
                resource="customer-comm-mediums-page"
              />
              <MenuItem
                title={t("v8_check_in_services", "Check-in Services")}
                icon="calendar check"
                link={PATHS.CHECK_IN_SERVICES_DASHBOARD}
                resource="check-in-service-page"
                activity="browse"
              />
              <MenuItem
                title={t("v8_agreements", "Agreements")}
                icon="square check"
                link={PATHS.AGREEMENTS_DASHBOARD}
                activity="browse"
                resource="check-in-agreements-page"
              />
              {!isAllLocationsSelected && selectedLocation?.is_kiosk_enabled && (
                <MenuItem title={t("v8_kiosk", "Kiosk")} icon="key" link={PATHS.KIOSK_DASHBOARD} activity="browse" resource="kiosk-page" />
              )}
              {!isAllLocationsSelected && selectedLocation?.is_keylocker_enabled && (
                <MenuItem title={t("v8_key_lockers", "Key Lockers")} icon="key" link={PATHS.KEYLOCKERS_DASHBOARD} activity="browse" resource="keylocker-page" />
              )}
              <MenuItem title={t("v8_sharebox_logs", "Sharebox Logs")} icon="key" link={PATHS.SHAREBOX_DASHBOARD} activity="browse" resource="sharebox-logs-page" />
              {!isAllLocationsSelected && selectedLocation?.is_acses_enabled && (
                <MenuItem title={t("v8_acses_logs", "Acses Logs")} icon="key" link={PATHS.ACSES_LOGS_DASHBOARD} activity="browse" resource="acses-events-page" />
              )}
              {!isAllLocationsSelected && selectedLocation?.send_parking_gate_code && (
                <MenuItem
                  title={t("v8_parking_gate_codes", "Parking Gate Codes")}
                  icon="square parking"
                  link={PATHS.PARKING_GATE_CODES_DASHBOARD}
                  activity="browse"
                  resource="parking-gate-code-page"
                />
              )}
            </MenuSection>
            <MenuSection title={t("v8_exact", "Exact")}>
              <MenuItem
                title={t("v8_exact_monitoring", "Exact Monitoring")}
                icon="file invoice dollar"
                link={PATHS.EXACT_MONITORING}
                resource="exact-monitoring-page"
                activity="browse"
              />
              <MenuItem
                title={t("v8_location_licenses", "Location Licenses")}
                icon="id card"
                link={PATHS.LOCATION_LICENSE_OVERVIEW}
                resource="exact-license-page"
                activity="browse"
              />
              <MenuItem
                title={t("v8_dealer_licenses", "Dealer Licenses")}
                icon="file certificate"
                link={PATHS.DEALER_LICENSE_OVERVIEW}
                resource="exact-dealer-license-page"
                activity="browse"
              />
              <MenuItem
                title={t("v8_unlinked_exact_licenses", "Unlinked Exact Licenses")}
                icon="link slash"
                link={PATHS.UNLINKED_EXACT_LICENSES}
                resource="exact-unlinked-licenses-page"
                activity="browse"
              />
            </MenuSection>
            <MenuSection title={t("v8_configuration", "Configuration")}>
              <MenuItem title={t("v8_notes_menu", "Notes")} icon="notes sticky" link={PATHS.NOTES_MENU} resource="note-comment-suggestions-page" activity="browse" />
              <MenuItem
                title={t("v8_pending_checklists", "Pending Checklists")}
                icon="screwdriver wrench"
                link={PATHS.PENDING_CHECKLISTS_DASHBOARD}
                resource="pending-checklist-page"
                activity="browse"
              />
              <MenuItem title={t("v8_checklists", "Checklists")} icon="list ol" link={PATHS.CHECKLISTS_DASHBOARD} resource="checklists-page" activity="browse" />
              <MenuItem title={t("v8_questions", "Questions")} icon="talk" link={PATHS.QUESTIONS_DASHBOARD} resource="questions-page" activity="browse" />
            </MenuSection>
            <MenuSection title={t("v8_overview", "Overview")}>
              <MenuItem
                title={t("v8_dealer_overview", "Dealer Overview")}
                icon="sitemap"
                link={PATHS.DEALER_OVERVIEW_DASHBOARD}
                resource="dealers-page"
                activity="browse"
              />
              <MenuItem
                title={t("v8_location_overview", "Location Overview")}
                icon="sitemap"
                link={PATHS.LOCATION_OVERVIEW_DASHBOARD}
                resource="locations-page"
                activity="browse"
              />
            </MenuSection>
            <MenuSection title={t("v8_claire_admin", "Claire Admin")}>
              <MenuItem title={t("v8_dealers", "Dealers")} icon="sitemap" link={PATHS.DEALERS_DASHBOARD} resource="admin-dealers-page" activity="browse" />
              <MenuItem
                title={t("v8_checklist_settings", "Checklist Settings")}
                icon="settings"
                link={PATHS.CHECKLIST_SETTINGS_DASHBOARD}
                resource="checklist-settings-page"
                activity="browse"
              />
              <MenuItem
                title={t("v8_factory_exports", "Factory Exports")}
                icon="mail"
                link={PATHS.FACTORY_EXPORTS_DASHBOARD}
                resource="factory-export-page"
                activity="browse"
              />
              <MenuItem title={t("v8_tablets", "Tablets")} icon="tablet screen button" link={PATHS.DEVICES_DASHBOARD} resource="devices-page" activity="browse" />
              <MenuItem title={t("v8_tyres", "Tyres")} icon="tire" link={PATHS.TYRES_DASHBOARD} resource="tyres-page" activity="browse" />
              <MenuItem
                title={t("v8_incomplete_tyres", "Incomplete Tyres")}
                icon="upload"
                link={PATHS.INCOMPLETE_TYRES_DASHBOARD}
                resource="tires-upload-page"
                activity="browse"
              />
              <MenuItem title={t("v8_brands", "Brands")} icon="car mirrors" link={PATHS.BRANDS_DASHBOARD} resource="brands-page" activity="browse" />
            </MenuSection>
            <MenuSection title={t("v8_super_admin", "Super Admin")}>
              <MenuItem title={t("v8_tags", "Tags")} icon="tags" link={PATHS.TAGS_DASHBOARD} resource="tags-page" activity="browse" />
              <MenuItem title={t("v8_status_list", "Status List")} icon="list" link={PATHS.STATUS_LIST_DASHBOARD} resource="status-list-global-page" activity="browse" />
              <MenuItem
                title={t("v8_report_templates", "Report Templates")}
                icon="file code"
                link={PATHS.REPORT_TEMPLATES_DASHBOARD}
                resource="templates-page"
                activity="browse"
              />
            </MenuSection>
          </MenuView>
          <SidebarPusher dimmed={showSidebarMenu} className="main-container-pusher-dimmed">
            <Segment basic className="main-container-segment">
              <NavActionsProvider>
                <NewsStripe />
                <NavBar openSidebar={() => setShowSidebarMenu(true)} />
                <div id="containerId" className={`content-container ${isAppointments ? "appointments-container" : ""} ${hasNewsStripe ? "with-news-stripe" : ""}`}>
                  {children}
                </div>
              </NavActionsProvider>
            </Segment>
          </SidebarPusher>
        </SidebarPushable>
      </div>
    </ErrorBoundary>
  );
};
